import { MARKETING_THEME } from "@/lib/theme";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "@/components/marketing/PageShell";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const usd = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: n % 1 ? 2 : 0 });

const PLANS = [
  { id: "starter", name: "Starter", tagline: "Pay as you go, no monthly fee.", unit: 9, field: 85,
    features: ["Records-based address check", "Email consent link for every subject", "Verified / Discrepant / Unverifiable verdict", "Evidence-backed report", "Up to 3 team seats"] },
  { id: "professional", name: "Professional", tagline: "For teams verifying every month.", monthly: 299, yearly: 3049.8, included: 40, field: 65, featured: true,
    features: ["40 verifications included each month", "Household composition checks", "Unlimited team seats with roles", "Case console and status tracking", "Priority email support"] },
  { id: "enterprise", name: "Enterprise", tagline: "High volume, carrier-grade.", monthly: 1499, yearly: 15289.8, included: 250, field: 55,
    features: ["250 verifications included each month", "Everything in Professional", "API access", "Custom retention schedule", "Dedicated account manager"] },
];

const BillingToggle = ({ billing, setBilling }) => (
  <div className="inline-flex rounded-full border border-gray-200 bg-white p-1" data-testid="billing-toggle">
    {[["monthly", "Monthly"], ["yearly", "Yearly · save 15%"]].map(([k, l]) => (
      <button key={k} onClick={() => setBilling(k)} data-testid={`billing-${k}`}
        className={`rounded-full px-4 py-2 font-mono-label text-[10px] font-semibold transition-colors ${billing === k ? "bg-oath-ink text-white" : "text-oath-secondary hover:text-oath-ink"}`}>{l}</button>
    ))}
  </div>
);

const PriceLine = ({ plan, billing }) => {
  if (plan.unit) return <><span className="font-display text-4xl font-bold">{usd(plan.unit)}</span><span className="ml-1 text-sm opacity-60">/ report</span></>;
  const amount = billing === "yearly" ? plan.yearly : plan.monthly;
  return (
    <>
      <span className="font-display text-4xl font-bold" data-testid={`price-${plan.id}`}>{usd(amount)}</span>
      <span className="ml-1 text-sm opacity-60">/ {billing === "yearly" ? "year" : "month"}</span>
      {billing === "yearly" && <div className="mt-1 text-xs opacity-60">{usd(plan.yearly / 12)} per month, billed yearly</div>}
    </>
  );
};

const PlanCard = ({ plan, billing, onBuy }) => {
  const dark = plan.featured;
  return (
    <div data-testid={`plan-${plan.id}`} className={`relative flex flex-col rounded-3xl border p-7 transition-transform duration-200 hover:-translate-y-1 ${dark ? "border-oath-navy bg-oath-navy text-white" : "border-gray-200 bg-white text-oath-ink"}`}>
      {dark && <span className="absolute -top-3 left-7 rounded-full bg-oath-accent px-3 py-1 font-mono-label text-[9px] font-semibold text-white">MOST POPULAR</span>}
      <h2 className="font-display text-xl font-bold">{plan.name}</h2>
      <p className={`mt-1 text-sm ${dark ? "text-white/60" : "text-oath-secondary"}`}>{plan.tagline}</p>
      <div className="mt-6"><PriceLine plan={plan} billing={billing} /></div>
      <ul className="mt-6 grid gap-2.5 text-sm">
        {plan.features.map((f) => <li key={f} className="flex gap-2"><Check className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "text-emerald-300" : "text-oath-accent"}`} />{f}</li>)}
        <li className="flex gap-2"><Check className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "text-emerald-300" : "text-oath-accent"}`} />Field visits {usd(plan.field)} each</li>
      </ul>
      <button onClick={() => onBuy(plan)} data-testid={`buy-${plan.id}`}
        className={`mt-8 rounded-full px-6 py-3.5 font-mono-label text-[11px] font-semibold transition-colors ${dark ? "bg-white text-oath-ink hover:bg-gray-100" : "bg-oath-ink text-white hover:bg-black"}`}>
        {plan.unit ? "BUY REPORTS" : "BUY PLAN"}
      </button>
    </div>
  );
};

const CustomCard = () => (
  <div className="flex flex-col justify-between gap-6 rounded-3xl border border-dashed border-gray-300 bg-white p-7 sm:flex-row sm:items-center" data-testid="plan-custom">
    <div>
      <h2 className="font-display text-xl font-bold text-oath-ink">Custom</h2>
      <p className="mt-1 max-w-xl text-sm text-oath-secondary">More than 1,000 verifications a month, state programs, special data sources or on-site field coverage? We'll build a plan around your volume and compliance needs.</p>
    </div>
    <Link to="/contact" data-testid="contact-sales" className="shrink-0 rounded-full border border-oath-ink px-6 py-3.5 text-center font-mono-label text-[11px] font-semibold text-oath-ink transition-colors hover:bg-oath-ink hover:text-white">CONTACT SALES</Link>
  </div>
);

const CheckoutDialog = ({ plan, billing, onClose }) => {
  const [form, setForm] = useState({ full_name: "", email: "", company: "", quantity: 10 });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const total = plan?.unit ? plan.unit * Number(form.quantity || 0) : plan && (billing === "yearly" ? plan.yearly : plan.monthly);

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { ...form, quantity: Number(form.quantity), package_id: plan.id, billing, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checkout-dialog" className={MARKETING_THEME}>
        <DialogHeader>
          <DialogTitle>{plan?.name} {plan?.unit ? "reports" : `· ${billing}`}</DialogTitle>
          <DialogDescription>You'll be taken to a secure Stripe page to pay by card.</DialogDescription>
        </DialogHeader>
        <form onSubmit={pay} className="grid gap-4">
          <div className="grid gap-2"><Label>Full name</Label><Input data-testid="checkout-name" value={form.full_name} onChange={set("full_name")} required minLength={2} /></div>
          <div className="grid gap-2"><Label>Work email</Label><Input data-testid="checkout-email" type="email" value={form.email} onChange={set("email")} required /></div>
          <div className="grid gap-2"><Label>Company</Label><Input data-testid="checkout-company" value={form.company} onChange={set("company")} required minLength={2} /></div>
          {plan?.unit && <div className="grid gap-2"><Label>Number of reports (5–500)</Label><Input data-testid="checkout-quantity" type="number" min={5} max={500} value={form.quantity} onChange={set("quantity")} required /></div>}
          <div className="flex items-center justify-between rounded-xl bg-oath-bg px-4 py-3 text-sm"><span>Total today</span><strong data-testid="checkout-total">{total ? usd(total) : "—"}</strong></div>
          <button type="submit" disabled={loading} data-testid="checkout-pay"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[11px] font-semibold text-white hover:bg-black disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "CONTINUE TO PAYMENT"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Pricing() {
  const [billing, setBilling] = useState("monthly");
  const [plan, setPlan] = useState(null);
  return (
    <PageShell eyebrow="— PRICING —" title="Simple, transparent pricing">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="max-w-xl text-sm text-oath-secondary">Priced below typical industry rates: field occupancy checks commonly run $40–$100 per visit. Every plan includes consent capture and an evidence-backed verdict.</p>
        <BillingToggle billing={billing} setBilling={setBilling} />
      </div>
      <div className="mt-10 grid gap-5 lg:grid-cols-3">
        {PLANS.map((p) => <PlanCard key={p.id} plan={p} billing={billing} onBuy={setPlan} />)}
      </div>
      <div className="mt-5"><CustomCard /></div>
      <p className="mt-6 text-xs text-oath-muted">Prices in USD. Plans are prepaid for the billing period chosen. Applicable taxes may be added at checkout. Verifications are only performed for approved organizations with a permissible purpose and subject consent.</p>
      <CheckoutDialog plan={plan} billing={billing} onClose={() => setPlan(null)} />
    </PageShell>
  );
}
